import { Fragment, type ReactNode } from 'react';
import { Icon } from '@iconify/react';
import { RadioGroup, Radio } from '@nextui-org/react';
import { CLAIM_INTAKE_GENERIC_ERROR_MESSAGE } from './claimSubmitErrorUtils';

// --- types ---
export type ContractData = {
  contractNumber: string;
  status: { code: string; description: string };
  effectiveDate: string;
  expirationDate: string;
  /** Date the contract was sold. */
  saleDate?: string;
  /** Date the vehicle first entered service (used as manufactured/in-service date for product payload). */
  inServiceDate?: string;
  dealerNumber: string;
  dealerName: string;
  dealerCost: { amount: number };
  retailCost: { amount: number };
  /** Payee info; `id` maps to `actor_id` in the product payload. */
  payee?: { id?: string };
  vehicle: {
    vin: string;
    make: string;
    model: string;
    vehicleYear: number;
    vehicleType: string;
  };
  customer: {
    firstName: string;
    lastName: string;
    phone?: string;
    address?: {
      address1?: string;
      address2?: string;
      city?: string;
      state?: string;
      postalCode?: string;
      countryCode?: string;
      country?: string;
      country_code?: string;
    };
  };
  coverage: {
    description: string;
    /** Present on contract lookup payloads; code is sent as `product_type` on claim generation. */
    productType?: { code?: string; description?: string };
    terms: Array<{
      deductible: { amount: number; type: string };
      termOdometer: number;
      termMonths: number;
      components?: Array<{
        code?: string;
        description?: string;
        isCovered?: boolean;
        is_covered?: boolean;
        lossCodes?: Array<{
          code?: string;
          description?: string;
          isCovered?: boolean;
          is_covered?: boolean;
          dealerCost?: number;
          coverageLossCodeId?: number;
        }>;
      }>;
    }>;
  };
  saleOdometer: number;
  effectiveOdometer: number;
  expirationOdometer: number;
};

/** One labor line row on a repair (pre-generation draft + claim generation payload). */
export type ClaimLaborLineRow = {
  id: number;
  labor_amount: string;
  labor_description: string;
  labor_hours: string;
  labor_op_code: string;
  allowFixedValue: boolean;
};

/** One part line row on a repair (pre-generation draft + claim generation payload). */
export type ClaimPartLineRow = {
  id: number;
  part_amount: string;
  part_description: string;
  part_number: string;
  part_quantity: string;
  part_unit_price: string;
};

/** One repair line (3Cs) in claim intake repairs step — shared across step 3 and review. */
export type ClaimRepairEntry = {
  id: number;
  complaint: string;
  cause: string;
  correction: string;
  /** Tire tread depth (0–32) — used when product type is TIRE. */
  tiretread: string;
  labor_lines: ClaimLaborLineRow[];
  part_lines: ClaimPartLineRow[];
};

export function isTireProductType(productType: string): boolean {
  const code = productType.trim().toUpperCase();
  return code === 'TIRE' || code === 'TWE';
}

/** Premier claim intake hides servicer confirmation and vehicle notes. */
export function isPremierTenant(orgName?: string | null): boolean {
  return (orgName ?? '').trim().toLowerCase() === 'premierpr';
}

/** Photo prompts shown as chips on Supporting Documents when the contract is TIRE / TWE. */
export const TWE_SUPPORTING_DOCUMENT_CHIPS = [
  'Odometer',
  'Damage to wheel(s)/tire(s)',
  'Damaged wheel(s)/tire(s) from the side',
  'Tire tread depth measurement for damaged tire(s)',
  'Overall photo(s) of the vehicle (same side as damaged wheel/tire)',
  'VIN placard',
  'Tire information placard',
] as const;

export function clampTireTreadInput(raw: string): string {
  const trimmed = raw.trim();
  if (!trimmed) return '';
  const n = Number.parseInt(trimmed, 10);
  if (!Number.isFinite(n)) return '';
  return String(Math.min(32, Math.max(0, n)));
}

let claimIntakeLineIdSeq = 1;
export function nextClaimIntakeLineId(): number {
  claimIntakeLineIdSeq += 1;
  return claimIntakeLineIdSeq;
}

export function resetClaimIntakeLineIdSeq(): void {
  claimIntakeLineIdSeq = 1;
}

export function makeEmptyLaborLineRow(id?: number): ClaimLaborLineRow {
  return {
    id: id ?? nextClaimIntakeLineId(),
    labor_amount: '',
    labor_description: '',
    labor_hours: '',
    labor_op_code: '',
    allowFixedValue: false,
  };
}

export function makeEmptyPartLineRow(id?: number): ClaimPartLineRow {
  return {
    id: id ?? nextClaimIntakeLineId(),
    part_amount: '',
    part_description: '',
    part_number: '',
    part_quantity: '',
    part_unit_price: '',
  };
}

/** Default repair row for step 3 (one blank labor + one blank part line). */
export function makeInitialClaimRepair(id = 1): ClaimRepairEntry {
  return {
    id,
    complaint: '',
    cause: '',
    correction: '',
    tiretread: '',
    labor_lines: [makeEmptyLaborLineRow()],
    part_lines: [makeEmptyPartLineRow()],
  };
}

function detailTypeLower(row: Record<string, unknown>): string {
  const raw = row.detailType;
  return typeof raw === 'string' ? raw.toLowerCase() : '';
}

/** Maps `claim_object.failures[i].details` labor/part rows back into draft line arrays after 3csmapper. */
export function extractDraftLinesFromFailure(failure: Record<string, unknown> | undefined): {
  labor_lines: ClaimLaborLineRow[];
  part_lines: ClaimPartLineRow[];
} {
  if (!failure) {
    return { labor_lines: [makeEmptyLaborLineRow()], part_lines: [makeEmptyPartLineRow()] };
  }
  const details = failure.details;
  if (!Array.isArray(details)) {
    return { labor_lines: [makeEmptyLaborLineRow()], part_lines: [makeEmptyPartLineRow()] };
  }
  const labor_lines: ClaimLaborLineRow[] = [];
  const part_lines: ClaimPartLineRow[] = [];
  for (const x of details) {
    if (x == null || typeof x !== 'object' || Array.isArray(x)) continue;
    const row = x as Record<string, unknown>;
    const dt = detailTypeLower(row);
    const requested =
      row.requested != null && typeof row.requested === 'object' && !Array.isArray(row.requested)
        ? (row.requested as Record<string, unknown>)
        : {};
    const lossCode =
      row.lossCode != null && typeof row.lossCode === 'object' && !Array.isArray(row.lossCode)
        ? (row.lossCode as Record<string, unknown>)
        : {};
    if (dt === 'labor') {
      labor_lines.push({
        id: nextClaimIntakeLineId(),
        labor_description: typeof row.description === 'string' ? row.description : String(row.description ?? ''),
        labor_hours:
          typeof requested.quantity === 'number'
            ? String(requested.quantity)
            : String(requested.quantity ?? ''),
        labor_amount:
          typeof requested.amount === 'number' ? String(requested.amount) : String(requested.amount ?? ''),
        labor_op_code: typeof lossCode.code === 'string' ? lossCode.code : String(lossCode.code ?? ''),
        allowFixedValue: false,
      });
    } else if (dt === 'part') {
      part_lines.push({
        id: nextClaimIntakeLineId(),
        part_description: typeof row.description === 'string' ? row.description : String(row.description ?? ''),
        part_number: typeof row.partNumber === 'string' ? row.partNumber : String(row.partNumber ?? ''),
        part_quantity:
          typeof requested.quantity === 'number'
            ? String(requested.quantity)
            : String(requested.quantity ?? ''),
        part_unit_price: '',
        part_amount:
          typeof requested.amount === 'number' ? String(requested.amount) : String(requested.amount ?? ''),
      });
    }
  }
  if (labor_lines.length === 0) labor_lines.push(makeEmptyLaborLineRow());
  if (part_lines.length === 0) part_lines.push(makeEmptyPartLineRow());
  return { labor_lines, part_lines };
}

function claimFailureScalarCell(v: unknown): string {
  if (v == null) return '-';
  if (typeof v === 'string') return v.trim() === '' ? '-' : v;
  if (typeof v === 'number' || typeof v === 'boolean' || typeof v === 'bigint') return String(v);
  return '-';
}

/** Failures array from a generated `claim_object` (legacy structure). */
export function getClaimObjectFailures(claimObject: Record<string, unknown> | null | undefined): Record<string, unknown>[] {
  const failures = claimObject?.failures;
  if (!Array.isArray(failures)) return [];
  return failures.filter((x): x is Record<string, unknown> => x != null && typeof x === 'object' && !Array.isArray(x));
}

/** Maps `claim_object.failures` into editable repair rows (3Cs + draft labor/part lines). */
export function mapClaimFailuresToRepairEntries(failures: Record<string, unknown>[]): ClaimRepairEntry[] {
  return failures.map((failure, i) => {
    const { labor_lines, part_lines } = extractDraftLinesFromFailure(failure);
    return {
      id: i + 1,
      complaint: claimFailureScalarCell(failure.complaint) === '-' ? '' : claimFailureScalarCell(failure.complaint),
      cause: claimFailureScalarCell(failure.cause) === '-' ? '' : claimFailureScalarCell(failure.cause),
      correction: claimFailureScalarCell(failure.correction) === '-' ? '' : claimFailureScalarCell(failure.correction),
      tiretread:
        claimFailureScalarCell(failure.tiretread) === '-'
          ? ''
          : claimFailureScalarCell(failure.tiretread),
      labor_lines,
      part_lines,
    };
  });
}

// ─── New unified API response format (requests[].parts / requests[].labor) ───

/** Extracts `requests` array from the unified claim / mapper response. */
export function getClaimRequests(claimObject: Record<string, unknown> | null | undefined): Record<string, unknown>[] {
  const requests = claimObject?.requests;
  if (!Array.isArray(requests)) return [];
  return requests.filter((x): x is Record<string, unknown> => x != null && typeof x === 'object' && !Array.isArray(x));
}

/** Replaces `requests` on the claim object (3csmapper / saveclaim unified shape). */
export function setClaimObjectRequests(
  claimObject: Record<string, unknown>,
  requests: Record<string, unknown>[]
): Record<string, unknown> {
  return { ...claimObject, requests };
}

export function isRequest3CsComplete(req: Record<string, unknown>): boolean {
  return Boolean(
    strCell(req.complaint_description).trim() &&
      strCell(req.cause_description).trim() &&
      strCell(req.corrective_action_description).trim()
  );
}

export function requestHasLineItems(req: Record<string, unknown>): boolean {
  const parts = Array.isArray(req.parts) ? req.parts : [];
  const labor = Array.isArray(req.labor) ? req.labor : [];
  return parts.length > 0 || labor.length > 0;
}

export type ClaimRequestLineDisplay = Readonly<{
  lineType: 'labor' | 'part';
  component: string;
  partNumber: string;
  description: string;
  quantity: string;
  amount: string;
  lossCode: string;
}>;

/** Flattened labor + part rows for read-only review tables. */
export function getRequestLineDisplayRows(req: Record<string, unknown>): ClaimRequestLineDisplay[] {
  const out: ClaimRequestLineDisplay[] = [];
  const labor = Array.isArray(req.labor) ? (req.labor as Record<string, unknown>[]) : [];
  const parts = Array.isArray(req.parts) ? (req.parts as Record<string, unknown>[]) : [];
  for (const l of labor) {
    out.push({
      lineType: 'labor',
      component: strCell(l.component),
      partNumber: '—',
      description: strCell(l.labor_description),
      quantity: strCell(l.labor_hours),
      amount: strCell(l.amount),
      lossCode: strCell(l.loss_code),
    });
  }
  for (const p of parts) {
    out.push({
      lineType: 'part',
      component: strCell(p.component),
      partNumber: strCell(p.part_number),
      description: strCell(p.part_description),
      quantity: strCell(p.part_quantity),
      amount: strCell(p.amount),
      lossCode: strCell(p.loss_code),
    });
  }
  return out;
}

function strCell(v: unknown): string {
  if (typeof v === 'string') return v;
  if (typeof v === 'number' || typeof v === 'boolean' || typeof v === 'bigint') return String(v);
  return '';
}

/** Maps `requests` (new unified API format) into editable repair rows. */
export function mapClaimRequestsToRepairEntries(requests: Record<string, unknown>[]): ClaimRepairEntry[] {
  return requests.map((req, i) => {
    const rawParts = Array.isArray(req.parts) ? req.parts : [];
    const rawLabor = Array.isArray(req.labor) ? req.labor : [];

    const part_lines: ClaimPartLineRow[] = (rawParts as Record<string, unknown>[]).map((p) => ({
      id: nextClaimIntakeLineId(),
      part_description: strCell(p.part_description),
      part_number: strCell(p.part_number),
      part_quantity: strCell(p.part_quantity),
      part_unit_price: strCell(p.unit_cost),
      part_amount: strCell(p.amount),
    }));

    const labor_lines: ClaimLaborLineRow[] = (rawLabor as Record<string, unknown>[]).map((l) => ({
      id: nextClaimIntakeLineId(),
      labor_description: strCell(l.labor_description),
      labor_hours: strCell(l.labor_hours),
      labor_amount: strCell(l.amount),
      labor_op_code: strCell(l.labor_opcode),
      allowFixedValue: false,
    }));

    if (part_lines.length === 0) part_lines.push(makeEmptyPartLineRow());
    if (labor_lines.length === 0) labor_lines.push(makeEmptyLaborLineRow());

    return {
      id: i + 1,
      complaint: strCell(req.complaint_description),
      cause: strCell(req.cause_description),
      correction: strCell(req.corrective_action_description),
      tiretread: strCell(req.tiretread),
      labor_lines,
      part_lines,
    };
  });
}

function preferNonEmptyPrevious(mapped: string, previous: string): string {
  return previous.trim() ? previous : mapped;
}

/**
 * Keeps in-progress repair edits when 3csmapper returns mapped rows (e.g. allow-fixed-value checkbox).
 */
export function mergeMappedRepairsWithUserEdits(
  previous: ClaimRepairEntry[],
  mapped: ClaimRepairEntry[]
): ClaimRepairEntry[] {
  return mapped.map((repair, repairIdx) => {
    const prevRepair = previous[repairIdx];
    if (!prevRepair) return repair;

    const labor_lines = repair.labor_lines.map((line, lineIdx) => {
      const prevLine = prevRepair.labor_lines[lineIdx];
      if (!prevLine?.allowFixedValue) return line;
      return {
        ...line,
        allowFixedValue: true,
        labor_description: preferNonEmptyPrevious(line.labor_description, prevLine.labor_description),
        labor_hours: prevLine.labor_hours,
        labor_amount: prevLine.labor_amount,
        labor_op_code: preferNonEmptyPrevious(line.labor_op_code, prevLine.labor_op_code),
      };
    });

    return {
      ...repair,
      complaint: preferNonEmptyPrevious(repair.complaint, prevRepair.complaint),
      cause: preferNonEmptyPrevious(repair.cause, prevRepair.cause),
      correction: preferNonEmptyPrevious(repair.correction, prevRepair.correction),
      tiretread: preferNonEmptyPrevious(repair.tiretread, prevRepair.tiretread),
      labor_lines,
    };
  });
}

/** Payload from claim-generation (step 3) used on review step and claim submission. */
export type ClaimGenerationReviewData = {
  sourceId: string;
  claimObject: Record<string, unknown>;
};

type ClaimLeftNavItem = {
  key: string;
  label: string;
  icon: string;
  active?: boolean;
  disabled?: boolean;
  suffix?: string;
};

// --- constants ---
export const STEPS = [
  { key: 'contract-lookup', label: 'Contract Lookup' },
  { key: 'vehicle-claims', label: 'Vehicle & Claims' },
  { key: 'repairs-docs', label: 'Repairs' },
  { key: 'review', label: 'Review' },
] as const;

export const coverageCellClass =
  'min-w-0 min-h-[5.5rem] border border-default-200 rounded-lg p-3 flex flex-col gap-1 bg-content1';

// --- formatters ---
export const VIN_LENGTH = 17;
export const VIN_SHORT_LENGTH = 8;

const ALPHANUMERIC_PATTERN = /^[A-Za-z0-9]+$/u;

/** Normalizes VIN keyboard input: uppercase alphanumeric only, max 17 characters. */
export function sanitizeVinInput(value: string): string {
  return value.replace(/[^A-Za-z0-9]/gu, '').toUpperCase().slice(0, VIN_LENGTH);
}

/** Validates manually entered VIN — exactly 8 or 17 alphanumeric characters. Empty is valid (optional field). */
export function validateManualVin(vin: string, label = 'VIN'): string | undefined {
  const trimmed = vin.trim();
  if (!trimmed) return undefined;
  if (trimmed.length !== VIN_LENGTH && trimmed.length !== VIN_SHORT_LENGTH) {
    return 'Please enter either the last 8 digits or the full 17-digit VIN.';
  }
  if (!ALPHANUMERIC_PATTERN.test(trimmed)) {
    return `${label} may only contain letters and numbers.`;
  }
  return undefined;
}

/** Returns a user-facing error when `vin` is not a valid manual VIN; otherwise `undefined`. */
export function validateVin(vin: string, label = 'VIN'): string | undefined {
  const trimmed = vin.trim();
  if (!trimmed) return `${label} is required.`;
  return validateManualVin(trimmed, label);
}

export type ContractLookupQueryBuildResult =
  | { params: Record<string, string>; vinError?: undefined; empty?: false }
  | { params: null; vinError: string; empty?: false }
  | { params: null; vinError?: undefined; empty: true };

/** Builds VIN lookup `query_parameters` — includes only non-empty, valid fields. */
export function buildContractLookupQueryParameters(
  vin: string,
  contractNumber: string,
  options?: { skipVinValidation?: boolean }
): ContractLookupQueryBuildResult {
  const trimmedVin = vin.trim();
  const trimmedContractNumber = contractNumber.trim();
  const params: Record<string, string> = {};

  if (trimmedVin) {
    if (!options?.skipVinValidation) {
      const vinError = validateManualVin(trimmedVin);
      if (vinError) return { params: null, vinError };
    }
    params.vin = trimmedVin;
  }

  if (trimmedContractNumber) {
    params.contractNumber = trimmedContractNumber;
  }

  if (Object.keys(params).length === 0) {
    return { params: null, empty: true };
  }

  return { params };
}

export function lookupErrorMessage(err: unknown, fallback: string): string {
  void err;
  void fallback;
  return CLAIM_INTAKE_GENERIC_ERROR_MESSAGE;
}

export function formatDate(dateStr?: string): string {
  if (!dateStr) return '—';
  // Extract the YYYY-MM-DD portion directly to avoid timezone-shift bugs
  // e.g. "2028-12-19T00:00:00" in a UTC+5:30 locale would become "2028-12-18" via toISOString()
  const datePart = dateStr.split('T')[0] ?? dateStr;
  if (/^\d{4}-\d{2}-\d{2}$/u.test(datePart)) return datePart;
  try {
    return new Date(dateStr).toISOString().split('T')[0];
  } catch {
    return dateStr;
  }
}

/** `YYYY-MM-DD` for `<input type="date" max="…">` in the user's local timezone. */
export function localDateInputMaxToday(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

const REPAIR_ORDER_DATE_MDY = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/u;
const REPAIR_ORDER_DATE_YMD = /^(\d{4})-(\d{2})-(\d{2})$/u;

/** Normalizes repair order dates to `MM/DD/YYYY` (API / form canonical format). */
export function normalizeRepairOrderDateToMdy(raw: string): string {
  const t = raw.trim();
  if (!t) return '';

  const mdy = REPAIR_ORDER_DATE_MDY.exec(t);
  if (mdy) {
    const mm = mdy[1].padStart(2, '0');
    const dd = mdy[2].padStart(2, '0');
    return `${mm}/${dd}/${mdy[3]}`;
  }

  const ymd = REPAIR_ORDER_DATE_YMD.exec(t);
  if (ymd) {
    return `${ymd[2]}/${ymd[3]}/${ymd[1]}`;
  }

  // Voice agent may return natural language dates (e.g. "December 20, 2025").
  const parsed = new Date(t);
  if (!Number.isNaN(parsed.getTime())) {
    const mm = String(parsed.getMonth() + 1).padStart(2, '0');
    const dd = String(parsed.getDate()).padStart(2, '0');
    return `${mm}/${dd}/${parsed.getFullYear()}`;
  }

  return t;
}

/** Converts `MM/DD/YYYY` or `YYYY-MM-DD` to comparable `YYYY-MM-DD`, or null when invalid. */
export function repairOrderDateToComparableYmd(raw: string): string | null {
  const t = raw.trim();
  if (!t) return null;

  const mdy = REPAIR_ORDER_DATE_MDY.exec(t);
  if (mdy) {
    const mm = mdy[1].padStart(2, '0');
    const dd = mdy[2].padStart(2, '0');
    return `${mdy[3]}-${mm}-${dd}`;
  }

  if (REPAIR_ORDER_DATE_YMD.test(t)) return t;
  return null;
}

export function sanitizeRepairOrderDateInput(value: string): string {
  return value.replace(/[^\d/]/g, '');
}

export function formatRepairOrderDateDisplay(dateStr?: string): string {
  if (!dateStr?.trim()) return '—';
  const normalized = normalizeRepairOrderDateToMdy(dateStr);
  return normalized || '—';
}

/** True when `YYYY-MM-DD` is after today (local calendar). */
export function isLocalDateAfterToday(ymd: string): boolean {
  const trimmed = ymd.trim();
  if (!/^\d{4}-\d{2}-\d{2}$/u.test(trimmed)) return false;
  return trimmed > localDateInputMaxToday();
}

/** True when a repair order date (`MM/DD/YYYY` or `YYYY-MM-DD`) is after today. */
export function isRepairOrderDateAfterToday(raw: string): boolean {
  const ymd = repairOrderDateToComparableYmd(raw);
  if (!ymd) return false;
  return ymd > localDateInputMaxToday();
}

export function formatCurrency(amount?: number): string {
  if (amount == null) return '—';
  return `$${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export function formatCustomerName(customer?: ContractData['customer'] | null): string {
  const first = customer?.firstName?.trim() ?? '';
  const last = customer?.lastName?.trim() ?? '';
  if (!first && !last) return '—';
  return [first, last].filter(Boolean).join(' ');
}

function isContractCoverageCovered(value: unknown): boolean {
  if (value === true || value === 1) return true;
  if (typeof value === 'string') return value.trim().toLowerCase() === 'true';
  return false;
}

function normalizeContractCoverageTerms(
  terms: ContractData['coverage']['terms'] | Record<string, unknown> | undefined
): Array<Record<string, unknown>> {
  if (Array.isArray(terms)) {
    return terms.filter((term): term is Record<string, unknown> => term != null && typeof term === 'object');
  }
  if (terms != null && typeof terms === 'object') return [terms as Record<string, unknown>];
  return [];
}

function pushContractSurchargeDescription(
  descriptions: string[],
  seen: Set<string>,
  description: unknown,
  fallbackCode?: unknown
): void {
  const text =
    (typeof description === 'string' ? description : '') ||
    (typeof fallbackCode === 'string' ? fallbackCode : '');
  const trimmed = text.trim();
  if (!trimmed || seen.has(trimmed)) return;
  seen.add(trimmed);
  descriptions.push(trimmed);
}

/**
 * Covered surcharge labels from contract lookup:
 * `coverage.terms[].components[].lossCodes[]` where `isCovered` is true (VIN API shape).
 * Also supports `isCovered` directly on a component when present.
 */
export function getContractCoveredSurchargeDescriptions(contract: ContractData): string[] {
  const descriptions: string[] = [];
  const seen = new Set<string>();
  const terms = normalizeContractCoverageTerms(
    contract.coverage?.terms as ContractData['coverage']['terms'] | Record<string, unknown> | undefined
  );

  for (const term of terms) {
    const components = term.components;
    if (!Array.isArray(components)) continue;

    for (const component of components) {
      if (component == null || typeof component !== 'object' || Array.isArray(component)) continue;
      const componentRecord = component as Record<string, unknown>;

      if (isContractCoverageCovered(componentRecord.isCovered ?? componentRecord.is_covered)) {
        pushContractSurchargeDescription(
          descriptions,
          seen,
          componentRecord.description,
          componentRecord.code
        );
      }

      const lossCodes = componentRecord.lossCodes;
      if (!Array.isArray(lossCodes)) continue;

      for (const lossCode of lossCodes) {
        if (lossCode == null || typeof lossCode !== 'object' || Array.isArray(lossCode)) continue;
        const lossCodeRecord = lossCode as Record<string, unknown>;
        if (!isContractCoverageCovered(lossCodeRecord.isCovered ?? lossCodeRecord.is_covered)) continue;
        pushContractSurchargeDescription(
          descriptions,
          seen,
          lossCodeRecord.description,
          lossCodeRecord.code
        );
      }
    }
  }

  return descriptions;
}

/** Vehicle arrival, at-shop (must be Yes), and reimbursement choices required before generate/submit. */
export function isVehicleTripIntakeComplete(
  arrivalMethod: string,
  atServiceCenter: string,
  reimbursement: string
): boolean {
  return (
    (arrivalMethod === 'driven' || arrivalMethod === 'towed') &&
    atServiceCenter === 'yes' &&
    (reimbursement === 'Check' || reimbursement === 'CONNEXPAY')
  );
}

// --- StepperHeader ---
export function StepperHeader({
  currentStep,
  completedSteps,
}: Readonly<{
  currentStep: number;
  completedSteps: number[];
}>) {
  return (
    <div className="hidden lg:flex items-center gap-0 p-2">
      {STEPS.map((step, idx) => {
        const done = completedSteps.includes(idx);
        const cur = idx === currentStep;

        let circleClass: string;
        if (done) {
          circleClass = 'bg-green-500 text-white';
        } else if (cur) {
          circleClass = 'bg-primary text-white';
        } else {
          circleClass = 'bg-default-200 text-default-500';
        }

        let labelClass: string;
        if (cur) {
          labelClass = 'font-semibold text-primary';
        } else if (done || idx < currentStep) {
          labelClass = 'text-default-700';
        } else {
          labelClass = 'text-default-400';
        }

        return (
          <Fragment key={step.key}>
            <div className="flex items-center gap-1.5">
              <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-semibold shrink-0 ${circleClass}`}>
                {done ? <Icon icon="mdi:check" width={14} /> : idx + 1}
              </div>
              <span className={`text-[13px] whitespace-nowrap ${labelClass}`}>{step.label}</span>
            </div>
            {idx < STEPS.length - 1 && <div className="w-8 mx-1 border-t border-dashed border-default-300" />}
          </Fragment>
        );
      })}
    </div>
  );
}

// --- shared UI ---
export function LabeledHorizontalRadios({
  label,
  value,
  onValueChange,
  options,
  error,
  required = true,
  id,
}: Readonly<{
  label: ReactNode;
  value: string;
  onValueChange: (v: string) => void;
  options: ReadonlyArray<{ value: string; label: ReactNode }>;
  error?: string;
  required?: boolean;
  id?: string;
}>) {
  return (
    <div id={id}>
      <p className="text-sm font-medium text-default-700 mb-2">
        {label}
        {required ? <span className="text-danger ml-0.5">*</span> : null}
      </p>
      <RadioGroup
        orientation="horizontal"
        value={value}
        onValueChange={onValueChange}
        isInvalid={Boolean(error)}
        classNames={{ wrapper: 'gap-6' }}
      >
        {options.map((o) => (
          <Radio key={o.value} value={o.value}>
            {o.label}
          </Radio>
        ))}
      </RadioGroup>
      {error ? <p className="text-tiny text-danger mt-1">{error}</p> : null}
    </div>
  );
}

type ActionSourceLike = Readonly<{ name?: string; source_id?: string }>;

/** Resolves an action `source_id` by exact name or case-insensitive keyword match. */
export function resolveActionSourceId(
  actionSources: readonly ActionSourceLike[],
  options: Readonly<{ exactNames?: string[]; nameIncludes?: string }>
): string {
  for (const exact of options.exactNames ?? []) {
    const hit = actionSources.find((a) => a.name?.trim() === exact);
    if (hit?.source_id) return hit.source_id;
  }
  const keyword = options.nameIncludes?.trim().toLowerCase();
  if (keyword) {
    const hit = actionSources.find((a) => a.name?.trim().toLowerCase().includes(keyword));
    if (hit?.source_id) return hit.source_id;
  }
  return '';
}

export function servicerPostalReady(postalCode: string): boolean {
  return postalCode.replace(/\D/g, '').length >= 5;
}

/** Phone lookup is ready only at exactly 10 digits (partial or longer values do not fetch). */
export function servicerPhoneReady(phone: string): boolean {
  return phone.replace(/\D/g, '').length === 10;
}

/**
 * Lookup key for servicer_info.
 * Name and postal are included together only when both are ready.
 * Phone digits are included only when there are exactly 10.
 */
export function buildServicerFetchKey(name: string, postalCode: string, phone: string): string {
  const trimmedName = name.trim();
  const trimmedPostal = postalCode.trim();
  const nameZipPart =
    trimmedName && servicerPostalReady(trimmedPostal) ? `${trimmedName}|${trimmedPostal}` : '';
  const phoneDigits = phone.replace(/\D/g, '');
  const phonePart = phoneDigits.length === 10 ? phoneDigits : '';
  return `${nameZipPart}|${phonePart}`;
}

/**
 * Refetch when a ready lookup input changes.
 * Dropping the phone below or above 10 digits does not refetch on its own.
 */
export function servicerLookupChanged(
  previousKey: string | null,
  nextKey: string,
  nameZipReady: boolean,
  phoneReady: boolean
): boolean {
  if (!nameZipReady && !phoneReady) return false;
  if (previousKey == null) return true;
  if (previousKey === nextKey) return false;

  const splitAt = previousKey.lastIndexOf('|');
  const prevNameZip = splitAt >= 0 ? previousKey.slice(0, splitAt) : previousKey;
  const prevPhone = splitAt >= 0 ? previousKey.slice(splitAt + 1) : '';
  const nextSplitAt = nextKey.lastIndexOf('|');
  const nextNameZip = nextSplitAt >= 0 ? nextKey.slice(0, nextSplitAt) : nextKey;
  const nextPhone = nextSplitAt >= 0 ? nextKey.slice(nextSplitAt + 1) : '';

  const nameZipChanged = nameZipReady && nextNameZip !== prevNameZip;
  const phoneChanged = phoneReady && nextPhone !== prevPhone;
  return nameZipChanged || phoneChanged;
}
